import { useState } from "react";

import type { ApiErrorBody } from "../../api/client";
import type { AppConfig } from "../../config";
import { useWrite } from "../../hooks/useWrite";
import { ForbiddenNotice, Gate } from "../authority/Gate";
import { Notice } from "../ui";
import { ConfirmDialog } from "../write/ConfirmDialog";
import { effectFromSeconds, WriteResult } from "../write/EffectTiming";
import { Button } from "../../f7";

interface BlockResponse extends ApiErrorBody {
  blocked?: boolean;
  reason?: string;
  effective_within_seconds?: number;
}

export type BlockScope =
  | { kind: "merchant"; reference: string }
  | { kind: "user"; reference: string; email: string }
  | { kind: "everywhere"; email: string };

/** The sentence that tells an operator what they are about to affect. */
function blastRadius(scope: BlockScope, blocking: boolean): string {
  const verb = blocking ? "will be refused" : "will be allowed again";
  switch (scope.kind) {
    case "merchant":
      return `Every user of merchant ${scope.reference} ${verb}.`;
    case "user":
      return `${scope.email} ${verb} at merchant ${scope.reference} only — they are unaffected at other merchants.`;
    case "everywhere":
      return `${scope.email} ${verb} at EVERY merchant, including merchants not listed in this console.`;
  }
}

function pathFor(scope: BlockScope): string {
  switch (scope.kind) {
    case "merchant":
      return `/mcp/admin/tenant/${encodeURIComponent(scope.reference)}`;
    case "user":
      return `/mcp/admin/tenant/${encodeURIComponent(scope.reference)}/user/${encodeURIComponent(scope.email)}`;
    case "everywhere":
      return `/mcp/admin/tenant/*/user/${encodeURIComponent(scope.email)}`;
  }
}

/**
 * Block or unblock, at one of three scopes.
 *
 * The tenant-level form sends ONLY `blocked` — never `tier` alongside it. That
 * endpoint accepts both and is not atomic across them, so sending one field
 * means one write and one outcome. It also respects the authority split:
 * `admin-support` holds `block:write` without `tenant:write`, and a combined
 * form would 403 them out of an action they are entitled to perform.
 */
export function BlockControl({
  config,
  scope,
  blocked,
  onDone,
}: {
  config: AppConfig;
  scope: BlockScope;
  blocked: boolean;
  onDone: () => void;
}) {
  const write = useWrite<BlockResponse>(config);
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState<BlockResponse | null>(null);

  const blocking = !blocked;
  const label = blocking ? "Block" : "Unblock";

  function submit(reason: string) {
    // Unblocking deletes the row, and the API discards any reason sent with it
    // — so the dialog does not ask for one. Collecting a string that is thrown
    // away teaches people the field is decorative.
    const body: Record<string, unknown> = { blocked: blocking };
    if (blocking && reason) body.reason = reason;

    void write
      .submit(pathFor(scope), { method: "POST", body: JSON.stringify(body) })
      .then((r) => {
        if (!r) return;
        setResult(r);
        setOpen(false);
        // Re-read rather than trusting the response: a lost reply is
        // indistinguishable from a failed write, client-side.
        onDone();
      });
  }

  return (
    <>
      <Gate need="block:write">
        {/* F7's own solid-red and outline treatments, not a className that
            fights them. The kit's Button already carries a radius, padding, a
            focus ring and a dark-mode pair; overriding those with
            `rounded-xs px-3 py-1.5` produced a control that matched nothing
            else on the page and lost its focus ring. Red is for the
            destructive direction only — unblocking is the way back. */}
        <Button color={blocking ? "red" : undefined} outline={!blocking} onClick={() => setOpen(true)}>
          {label}
          {scope.kind === "everywhere" ? " everywhere" : ""}
        </Button>
      </Gate>

      {write.state.kind === "denied" && (
        <div className="mt-2">
          <ForbiddenNotice message={write.state.error.message} />
        </div>
      )}
      {write.state.kind === "invalid" && (
        <div className="mt-2">
          <Notice tone="red" title="Refused">
            {write.state.error.message}
          </Notice>
        </div>
      )}

      {result && (
        <div className="mt-3">
          <WriteResult
            title={result.blocked ? "Blocked" : "Unblocked"}
            onDismiss={() => setResult(null)}
          >
            <p>
              {blastRadius(scope, Boolean(result.blocked))}{" "}
              {effectFromSeconds(result.effective_within_seconds, "This")}
            </p>
          </WriteResult>
        </div>
      )}

      <ConfirmDialog
        open={open}
        title={`${label} ${scope.kind === "merchant" ? `merchant ${scope.reference}` : scope.email}`}
        confirmLabel={label}
        destructive={blocking}
        requireReason={blocking}
        reasonHint="e.g. abuse report, non-payment"
        busy={write.state.kind === "submitting"}
        onCancel={() => setOpen(false)}
        onConfirm={submit}
        effect={
          blocking
            ? "The running server refreshes its blocklist snapshot about every 60 seconds, so this takes effect within ~1 minute."
            : "Unblocking deletes the row; the server picks it up within ~1 minute."
        }
      >
        <p>{blastRadius(scope, blocking)}</p>
        {scope.kind === "everywhere" && blocking && (
          <p className="text-amber-700 dark:text-amber-400">
            This is the widest block available. It is not tied to any merchant.
          </p>
        )}
        {scope.kind === "user" && !blocking && (
          <p>
            This lifts only the row for this merchant. If they are also blocked everywhere,
            or the whole merchant is blocked, they stay refused.
          </p>
        )}
      </ConfirmDialog>
    </>
  );
}
